/**
 * The herd (P2-TR3, NFR-1).
 *
 * Two presentations of the same herd, chosen by width.
 *
 * On a phone, a card grid: a table of tags and weights is what a spreadsheet
 * gives you, and what a partner in a shed actually recognises is the animal,
 * so the photo carries the card.
 *
 * At `md` and above, a table — because an admin comparing forty animals wants
 * to scan a column, and a thumbnail is enough to recognise one when the tag is
 * beside it. Same rows, same filters, same link target; only the shape differs.
 *
 * Photos load lazily and at an explicit size. On a 3G connection a grid of
 * forty full-size photos is most of a megabyte before anything is readable,
 * and `loading="lazy"` with fixed dimensions means the layout never jumps as
 * they arrive (NFR-3).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ProtectedImage } from '../../components/ProtectedImage.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { ageMonthsFromDays } from './animalAge.js';
import { useAnimals, type Animal } from './api.js';

const SPECIES_FILTERS = ['all', 'goat', 'cattle'] as const;
const STATUS_STYLE: Record<string, string> = {
  active: 'bg-green-100 text-green-900',
  sold: 'bg-blue-100 text-blue-900',
  died: 'bg-red-100 text-red-900',
  qurbani_own_use: 'bg-purple-100 text-purple-900',
};

function AnimalCard({ animal }: { animal: Animal }): JSX.Element {
  const { t } = useTranslation();
  const ageMonths = ageMonthsFromDays(animal.ageDays);

  return (
    <Link
      to={`/animals/${String(animal.id)}`}
      className="flex min-h-touch flex-col overflow-hidden rounded-xl bg-white shadow-sm"
    >
      <div className="aspect-square w-full bg-gray-100">
        {animal.photoUrl !== null ? (
          <ProtectedImage
            uploadId={animal.photoUrl}
            alt={animal.tag}
            width={180}
            height={180}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          // A placeholder rather than an empty box: an unphotographed animal
          // should still look like a card, not like a loading failure.
          <div className="flex h-full w-full items-center justify-center text-3xl text-gray-300" aria-hidden="true">
            {animal.species === 'cattle' ? '🐄' : '🐐'}
          </div>
        )}
      </div>

      <div className="p-2">
        <div className="flex items-baseline justify-between gap-1">
          <span className="font-medium text-gray-900">{animal.tag}</span>
          {animal.latestWeightKg !== null && (
            <span className="text-xs tabular-nums text-gray-500">{animal.latestWeightKg} kg</span>
          )}
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-1">
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${STATUS_STYLE[animal.status] ?? 'bg-gray-100'}`}>
            {t(`animals.status.${animal.status}`)}
          </span>
          {animal.isQurbaniTarget && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-900">
              {t('animals.qurbani')}
            </span>
          )}
        </div>

        {ageMonths !== null && (
          <p className="mt-1 text-xs text-gray-500">{t('animals.ageMonths', { count: ageMonths })}</p>
        )}
      </div>
    </Link>
  );
}

/** One herd row on desktop. The thumbnail keeps the animal recognisable. */
function AnimalRow({ animal }: { animal: Animal }): JSX.Element {
  const { t } = useTranslation();

  return (
    <tr className="hover:bg-gray-50">
      <td>
        <Link to={`/animals/${String(animal.id)}`} className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100">
            {animal.photoUrl !== null ? (
              <ProtectedImage
                uploadId={animal.photoUrl}
                alt=""
                width={36}
                height={36}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <span aria-hidden="true">{animal.species === 'cattle' ? '🐄' : '🐐'}</span>
            )}
          </span>
          <span className="font-medium text-gray-900">{animal.tag}</span>
        </Link>
      </td>

      <td>{t(`animals.species.${animal.species}`)}</td>
      <td>{t(`animals.sex.${animal.sex}`)}</td>

      <td>
        <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[animal.status] ?? 'bg-gray-100'}`}>
          {t(`animals.status.${animal.status}`)}
        </span>
        {animal.isQurbaniTarget && (
          <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">
            {t('animals.qurbani')}
          </span>
        )}
      </td>

      <td className="num">{animal.latestWeightKg ?? '—'}</td>
      <td className="num">{ageMonthsFromDays(animal.ageDays) ?? '—'}</td>
    </tr>
  );
}

export function AnimalList(): JSX.Element {
  const { t } = useTranslation();
  const [species, setSpecies] = useState<(typeof SPECIES_FILTERS)[number]>('all');
  const [showLeft, setShowLeft] = useState(false);

  const { data, isLoading, isError, refetch } = useAnimals({
    ...(species === 'all' ? {} : { species }),
    // Sold and died animals stay in the herd list only when asked for: their
    // history matters, but a partner counting animals wants the ones present.
    ...(showLeft ? {} : { status: 'active' }),
    limit: 100,
  });

  return (
    <div className="page-container">
      <div className="flex items-baseline justify-between py-4">
        <h1 className="text-xl font-semibold text-gray-800">{t('animals.title')}</h1>
        {data && <span className="text-sm text-gray-500">{t('animals.count', { count: data.total })}</span>}
      </div>

      <div className="mb-3 flex gap-2" role="group" aria-label={t('animals.filter')}>
        {SPECIES_FILTERS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setSpecies(option)}
            aria-pressed={species === option}
            className={`min-h-touch flex-1 rounded-lg px-3 py-2 text-sm ${
              species === option ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t(`animals.species.${option}`)}
          </button>
        ))}
      </div>

      <label className="mb-4 flex min-h-touch items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={showLeft} onChange={(e) => setShowLeft(e.target.checked)} className="h-4 w-4" />
        {t('animals.showLeft')}
      </label>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {data?.items.length === 0 && <p className="py-8 text-center text-gray-500">{t('animals.empty')}</p>}

      {/* Phone: two columns at 360px. Three would make each photo too small
          to recognise, which defeats the point of showing one. */}
      <div className="grid grid-cols-2 gap-3 md:hidden">
        {data?.items.map((animal) => <AnimalCard key={animal.id} animal={animal} />)}
      </div>

      {/* Desktop: the same rows as a scannable table. */}
      {data !== undefined && data.items.length > 0 && (
        <div className="hidden overflow-x-auto rounded-xl bg-white shadow-sm md:block">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('animals.tag')}</th>
                <th>{t('animals.speciesLabel')}</th>
                <th>{t('animals.sexLabel')}</th>
                <th>{t('animals.statusLabel')}</th>
                <th className="num">{t('animals.weightKg')}</th>
                <th className="num">{t('animals.ageLabel')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((animal) => (
                <AnimalRow key={animal.id} animal={animal} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Two ways in, because they are two different jobs: one animal with its
          age and its mother, or a haat trip whose cost is split across several
          (BR-14). The trip is the secondary action — a calving happens more
          often than a buying trip. */}
      <Link
        to="/animals/new"
        className="mt-6 flex min-h-touch items-center justify-center rounded-xl bg-green-700 px-4 py-3 font-medium text-white"
      >
        {t('animals.add.open')}
      </Link>

      <Link
        to="/animals/trip"
        className="mt-2 flex min-h-touch items-center justify-center rounded-xl bg-white px-4 py-3 font-medium text-gray-700 shadow-sm"
      >
        {t('animals.recordTrip')}
      </Link>
    </div>
  );
}
